import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CTASection = () => {
  const [selectedAction, setSelectedAction] = useState('assessment');

  const actionOptions = [
    {
      id: 'assessment',
      title: 'Évaluation Gratuite',
      subtitle: 'Diagnostic Complet des Compétences',
      description: 'Obtenez une analyse personnalisée de vos compétences actuelles et identifiez vos axes de développement prioritaires.',
      icon: 'CheckCircle',
      color: 'green',
      benefits: [
        'Évaluation 360° complète en 15 minutes',
        'Rapport détaillé avec recommandations',
        'Cartographie des compétences critiques',
        'Plan de développement personnalisé'
      ],
      action: 'Commencer l\'Évaluation',
      popular: true
    },
    {
      id: 'consultation',
      title: 'Consultation Stratégique',
      subtitle: 'Accompagnement Expert Personnalisé',
      description: 'Échangez avec nos experts pour définir une stratégie de développement des compétences adaptée à vos enjeux organisationnels.',
      icon: 'Users',
      color: 'blue',
      benefits: [
        'Session de 60 minutes avec un expert',
        'Analyse contextualisée de vos défis',
        'Recommandations stratégiques ciblées',
        'Feuille de route de transformation'
      ],
      action: 'Réserver Consultation',
      popular: false
    },
    {
      id: 'program',
      title: 'Programme Sur-Mesure',
      subtitle: 'Formation Intensive Personnalisée',
      description: 'Accédez à nos programmes de formation les plus avancés, conçus spécifiquement pour votre secteur et vos objectifs.',
      icon: 'Award',
      color: 'purple',
      benefits: [
        'Programme adapté à votre industrie',
        'Formateurs experts sectoriels',
        'Certification professionnelle reconnue',
        'Suivi post-formation inclus'
      ],
      action: 'Découvrir les Programmes',
      popular: false
    }
  ];

  const currentOption = actionOptions?.find(option => option?.id === selectedAction);

  const stats = [
    { label: 'Organisations Accompagnées', value: '500+', icon: 'Building' },
    { label: 'Professionnels Formés', value: '15,000+', icon: 'Users' },
    { label: 'Taux de Satisfaction', value: '96%', icon: 'Heart' },
    { label: 'ROI Moyen Formation', value: '340%', icon: 'TrendingUp' }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-6">Transformez Vos Compétences Dès Aujourd'hui</h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Rejoignez les leaders de l'industrie automobile qui ont choisi CRF PERFECTION 
            pour accélérer leur développement et celui de leurs équipes.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
          {stats?.map((stat, index) => (
            <div key={index} className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={stat?.icon} size={32} className="text-white" />
              </div>
              <div className="text-3xl font-bold text-white mb-2">{stat?.value}</div>
              <div className="text-blue-200 text-sm">{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Action Selection */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {actionOptions?.map((option) => (
            <button
              key={option?.id}
              onClick={() => setSelectedAction(option?.id)}
              className={`relative px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                selectedAction === option?.id
                  ? `bg-${option?.color}-600 text-white shadow-lg transform scale-105`
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {option?.popular && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                  Populaire
                </span>
              )}
              <div className="flex items-center space-x-3">
                <Icon name={option?.icon} size={24} />
                <span>{option?.title}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Selected Action Details */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-6">
              <div>
                <h3 className="text-3xl font-bold text-white mb-2">{currentOption?.title}</h3>
                <p className={`text-${currentOption?.color}-200 text-lg font-medium mb-4`}>
                  {currentOption?.subtitle}
                </p>
                <p className="text-gray-200 leading-relaxed text-lg">
                  {currentOption?.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentOption?.benefits?.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className={`text-${currentOption?.color}-400 flex-shrink-0 mt-1`} />
                    <span className="text-gray-200">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-4 text-center">
              <div className={`w-32 h-32 bg-${currentOption?.color}-600/30 rounded-full flex items-center justify-center mx-auto mb-6`}>
                <Icon name={currentOption?.icon} size={64} className={`text-${currentOption?.color}-300`} />
              </div>
              <button className={`bg-${currentOption?.color}-600 hover:bg-${currentOption?.color}-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors duration-200 shadow-lg hover:shadow-xl`}>
                {currentOption?.action}
              </button>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
            <h4 className="text-2xl font-bold text-white mb-6">Vous Préférez Nous Parler Directement ?</h4>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-600/30 rounded-lg flex items-center justify-center">
                  <Icon name="Phone" size={24} className="text-green-300" />
                </div>
                <div>
                  <div className="text-white font-semibold">Téléphone</div>
                  <div className="text-blue-200">+33 1 XX XX XX XX</div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-600/30 rounded-lg flex items-center justify-center">
                  <Icon name="Mail" size={24} className="text-blue-300" />
                </div>
                <div>
                  <div className="text-white font-semibold">Email</div>
                  <div className="text-blue-200">competences@crfperfection.com</div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-purple-600/30 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={24} className="text-purple-300" />
                </div>
                <div>
                  <div className="text-white font-semibold">Disponibilité</div>
                  <div className="text-blue-200">Lun-Ven 9h-18h</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
            <h4 className="text-2xl font-bold text-white mb-6">Nos Garanties</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <Icon name="Shield" size={24} className="text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <div className="text-white font-semibold">Satisfaction Garantie</div>
                  <div className="text-blue-200 text-sm">100% satisfait ou remboursé sous 30 jours</div>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Icon name="Award" size={24} className="text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <div className="text-white font-semibold">Expertise Certifiée</div>
                  <div className="text-blue-200 text-sm">Formateurs accrédités avec 15+ années d'expérience</div>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Icon name="RefreshCw" size={24} className="text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <div className="text-white font-semibold">Mise à Jour Continue</div>
                  <div className="text-blue-200 text-sm">Contenu actualisé selon les dernières tendances</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12">
          <h3 className="text-3xl font-bold text-white mb-4">L'Excellence N'Attend Pas</h3>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Chaque jour de retard dans le développement des compétences est une opportunité manquée. 
            Commencez votre transformation dès maintenant.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors duration-200 shadow-lg">
              Démarrer Maintenant
            </button>
            <button className="border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-200">
              Télécharger Guide PDF
            </button>
          </div>
          <p className="text-blue-200 text-sm mt-6">
            ✓ Sans engagement • ✓ Résultats immédiats • ✓ Support expert inclus
          </p>
        </div>
      </div>
    </section>
  );
};

export default CTASection;